import React, { useEffect, useState } from 'react';
import { NextPage } from 'next';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useAuth } from '../src/contexts/AuthContext';
import { RequireGuest } from '../src/components/auth/AuthGuard';
import { getPageTitle, getMetaDescription } from '../src/lib/config';
import AppLogo from '../src/components/common/AppLogo';
import { authAPI } from '../src/api/client';
import { resolvePostLoginDestination } from '../src/lib/companyContext';
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Flex,
  useColorModeValue,
  Icon,
  InputGroup,
  InputRightElement,
  IconButton,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { useToast } from '../src/hooks/useAppToast';
import { EmailIcon, LockIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';

const LoginPage: NextPage = () => {
  const router = useRouter();
  const { user, employee, loading, authenticated, checkUserStatus, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const toast = useToast();

  // カラーモード対応
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  useEffect(() => {
    // URLパラメータからエラーメッセージを確認
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined;
    if (params?.get('error') === 'unauthorized') {
      toast({
        title: '認証が必要です',
        description: 'ログインが必要です。メールアドレスとパスワードを入力してください。',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }

    const checkLoginAccess = async () => {
      // 認証済みユーザーの場合、状態をチェックして適切なページにリダイレクト
      if (authenticated && user) {
        try {
          // LINE-only ユーザー（email なし）は直接ダッシュボードへリダイレクト
          if (!user.email) {
            // 行き先はログイン・LINE のログイン・店選びの画面と同じ判定（2026-10-18）。
            // 🔴 以前はここだけ redirect を役割も外部かも見ずに使っていた（/login?redirect=//evil.example で外へ飛べた）
            const redirect = router.query.redirect as string;
            router.push(resolvePostLoginDestination(employee?.role_type ?? null, null, redirect, { isStaff: !!(employee as any)?.is_staff }));
            return;
          }

          const statusData = await checkUserStatus(user.email);

          if (!statusData.email_verified) {
            // メール未認証の場合は認証メール再送信ページにリダイレクト
            router.push(`/email-verification?email=${encodeURIComponent(user.email)}`);
          } else if (statusData.needs_setup) {
            // セットアップ未完了の場合はセットアップページにリダイレクト
            router.push(`/setup?email=${encodeURIComponent(user.email)}`);
          } else {
            // セットアップ完了の場合はロール別ダッシュボードにリダイレクト。
            // redirect がある場合でも、ユーザーのロールに合わないセクション・外部の URL は使わない（ログインの後と同じ判定・2026-10-18）
            const redirect = router.query.redirect as string;
            router.push(resolvePostLoginDestination(employee?.role_type ?? null, null, redirect));
          }
        } catch (error) {
          console.error('Login access check error:', error);
          // エラーの場合は認証状態をクリアしてログインページに留まる
          localStorage.removeItem('auth_token');
          document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
          window.location.reload();
        }
      }
    };

    if (!loading) {
      checkLoginAccess();
    }
  }, [authenticated, user, employee, loading, checkUserStatus, router, toast]);

  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    if (!password.trim()) {
      newErrors.password = 'パスワードを入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({}); // エラーをリセット
    
    try {
      console.log('🔐 Login attempt:', { email });
      
      const result = await login(email, password);
      
      // エラーオブジェクトが返された場合
      if (result && 'error' in result) {
        const error = result.error;
        console.warn('Login error in handleSubmit:', {
          message: error?.message,
          code: error?.code,
          status: error?.response?.status,
          url: error?.config?.url,
        });
        
        // タイムアウトエラーの処理
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.isTimeout) {
          console.warn('Timeout Error Details:', {
            code: error.code,
            message: error.message,
            url: error?.config?.url,
            timeout: error?.config?.timeout,
          });
          
          toast({
            title: '接続タイムアウト',
            description: 'サーバーとの接続がタイムアウトしました。しばらく時間をおいて再度お試しください。',
            status: 'error',
            duration: 8000,
            isClosable: true,
          });
          return;
        }
        
        // 422エラーの場合、フィールド別エラーを設定
        if (error.response?.status === 422) {
          const validationErrors = error.response?.data?.errors || {};
          const newErrors: { email?: string; password?: string } = {};
          
          if (validationErrors.email) {
            newErrors.email = Array.isArray(validationErrors.email) 
              ? validationErrors.email[0] 
              : validationErrors.email;
          }
          
          if (validationErrors.password) {
            newErrors.password = Array.isArray(validationErrors.password) 
              ? validationErrors.password[0] 
              : validationErrors.password;
          }
          
          setErrors(newErrors);
          
          // フィールド別エラーがある場合は、そのエラーメッセージをトーストで表示
          let toastMessage = '入力内容に誤りがあります';
          if (newErrors.email) {
            toastMessage = newErrors.email;
          } else if (newErrors.password) {
            toastMessage = newErrors.password;
          }
          
          toast({
            title: 'ログインエラー',
            description: toastMessage,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          
          return;
        }
        
        // ネットワークエラーの処理
        if (!error.response || error.isNetworkError) {
          toast({
            title: 'ネットワークエラー',
            description: 'サーバーに接続できませんでした。インターネット接続を確認してください。',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        
        // その他のエラーメッセージをトーストで表示
        const errorMessage = error.response?.data?.message || error.message || 'ログインに失敗しました';
        toast({
          title: 'エラー',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      console.log('✅ Login successful');
      // ログイン成功時はAuthContextでリダイレクト処理される
    } catch (error: any) {
      // 予期しないエラーの場合（Promise rejectionなど）
      console.warn('Unexpected error in handleSubmit:', {
        message: error?.message,
        code: error?.code,
        status: error?.response?.status,
        url: error?.config?.url,
      });
      
      // エラーの種類に応じて適切なメッセージを表示
      let errorMessage = '予期しないエラーが発生しました。';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.isTimeout) {
        errorMessage = 'サーバーとの接続がタイムアウトしました。しばらく時間をおいて再度お試しください。';
      } else if (!error.response || error.isNetworkError) {
        errorMessage = 'サーバーに接続できませんでした。インターネット接続を確認してください。';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'エラー',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLineLogin = async () => {
    const channelId = process.env.NEXT_PUBLIC_LINE_CHANNEL_ID;
    if (!channelId) {
      toast({
        title: 'LINE ログイン設定エラー',
        description: 'LINE ログインが設定されていません。管理者にお問い合わせください。',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // 通常ログイン開始時は招待復帰用バックアップを掃除
    // (古い招待トークンが残っていると、失敗時に誤って招待ページへ誘導してしまう)
    try {
      localStorage.removeItem('line_invite_token');
    } catch {
      // noop
    }

    // サーバーサイドで HMAC 署名付き state を生成（ブラウザ間でも検証可能）
    let state: string;
    try {
      const res = await authAPI.lineGenerateState();
      state = res.state;
    } catch {
      toast({
        title: 'エラー',
        description: 'LINE ログインの準備に失敗しました。もう一度お試しください。',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // redirect パラメータがあれば保存
    const searchParams = new URLSearchParams(window.location.search);
    const redirect = searchParams.get('redirect');
    // オープンリダイレクト防止: 相対パスのみ保存
    if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
      localStorage.setItem('line_oauth_redirect', redirect);
    }

    const callbackUrl = `${window.location.origin}/auth/line/callback`;
    const lineAuthUrl = new URL('https://access.line.me/oauth2/v2.1/authorize');
    lineAuthUrl.searchParams.set('response_type', 'code');
    lineAuthUrl.searchParams.set('client_id', channelId);
    lineAuthUrl.searchParams.set('redirect_uri', callbackUrl);
    lineAuthUrl.searchParams.set('state', state);
    lineAuthUrl.searchParams.set('scope', 'profile openid');
    lineAuthUrl.searchParams.set('openExternalBrowser', '1');
    lineAuthUrl.searchParams.set('bot_prompt', 'aggressive');

    window.location.href = lineAuthUrl.toString();
  };

  const handleInputChange = (field: 'email' | 'password', value: string) => {
    if (field === 'email') {
      setEmail(value);
    } else {
      setPassword(value);
    }
    
    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // 認証済みユーザーの場合はローディング表示
  if (loading || (authenticated && user)) {
    return (
      <>
        <Head>
          <title>{getPageTitle('ログイン')}</title>
          <meta name="description" content={getMetaDescription('ログインページです')} />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Head>
        <Center minH="100vh">
          <Box textAlign="center">
            <Spinner size="xl" color="blue.500" mb={4} />
            <Text color="gray.600">読み込み中...</Text>
          </Box>
        </Center>
      </>
    );
  }

  return (
    <RequireGuest>
              <Head>
          <title>{getPageTitle('ログイン')}</title>
          <meta name="description" content={getMetaDescription('ログインページです')} />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Head>

      <Box minH="100vh" bg={bgColor} py={{ base: 4, md: 8 }}>
        <Flex justify="center" align="center" px={{ base: 2, md: 4 }}>
          <Card 
            maxW={{ base: "full", md: "lg" }}
            w="full" 
            borderRadius={{ base: "lg", md: "xl" }}
            boxShadow="xl" 
            border="1px solid" 
            borderColor={borderColor}
            bg={cardBg}
            transition="all 0.3s ease"
            _hover={{
              boxShadow: '2xl',
              transform: 'translateY(-2px)',
            }}
          >
            <CardBody p={{ base: 6, md: 8 }}>
              {/* ロゴとタイトル */}
              <VStack spacing={6} mb={8}>
                <AppLogo />
              </VStack>

              {/* ログインフォーム */}
              <VStack spacing={6} align="stretch">
                <Text fontSize="lg" fontWeight="medium" textAlign="center">
                  アカウントにログイン
                </Text>
                
                <form onSubmit={handleSubmit}>
                  <VStack spacing={4}>
                    <FormControl isInvalid={!!errors.email}>
                      <FormLabel>メールアドレス</FormLabel>
                      <InputGroup>
                        <Input
                          type="email"
                          placeholder="example@company.com"
                          value={email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          size="lg"
                          borderRadius="lg"
                          borderColor={errors.email ? 'red.300' : 'gray.200'}
                          _focus={{
                            borderColor: 'blue.400',
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                          }}
                          _hover={{
                            borderColor: 'gray.300',
                          }}
                          disabled={isLoading}
                        />
                        <InputRightElement>
                          <Icon as={EmailIcon} color="gray.400" />
                        </InputRightElement>
                      </InputGroup>
                      {errors.email && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.email}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl isInvalid={!!errors.password}>
                      <FormLabel>パスワード</FormLabel>
                      <InputGroup>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="パスワードを入力"
                          value={password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          size="lg"
                          borderRadius="lg"
                          borderColor={errors.password ? 'red.300' : 'gray.200'}
                          _focus={{
                            borderColor: 'blue.400',
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                          }}
                          _hover={{
                            borderColor: 'gray.300',
                          }}
                          disabled={isLoading}
                        />
                        <InputRightElement>
                          <HStack spacing={1}>
                            <Icon as={LockIcon} color="gray.400" />
                            <IconButton
                              aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                              icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                              onClick={() => setShowPassword(!showPassword)}
                              variant="ghost"
                              size="sm"
                              color="gray.400"
                              _hover={{ color: 'gray.600' }}
                            />
                          </HStack>
                        </InputRightElement>
                      </InputGroup>
                      {errors.password && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.password}
                        </Text>
                      )}
                    </FormControl>

                    <Button
                      type="submit"
                      colorScheme="blue"
                      size="lg"
                      w="full"
                      isLoading={isLoading}
                      loadingText="ログイン中..."
                      borderRadius="lg"
                      fontWeight="semibold"
                      _hover={{
                        transform: 'translateY(-1px)',
                        boxShadow: 'lg',
                      }}
                      transition="all 0.2s"
                      disabled={isLoading}
                    >
                      ログイン
                    </Button>
                  </VStack>
                </form>

                {/* 区切り線 */}
                <HStack spacing={3}>
                  <Box flex="1" h="1px" bg="gray.300" />
                  <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">または</Text>
                  <Box flex="1" h="1px" bg="gray.300" />
                </HStack>

                {/* LINEでログイン */}
                <Button
                  size="lg"
                  w="full"
                  bg="#06C755"
                  color="white"
                  borderRadius="lg"
                  fontWeight="semibold"
                  _hover={{
                    bg: '#05b04c',
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg',
                  }}
                  transition="all 0.2s"
                  onClick={handleLineLogin}
                  disabled={isLoading}
                  leftIcon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                    </svg>
                  }
                >
                  LINEでログイン
                </Button>

                {/* LINE 登録者向けの案内 */}
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  LINE で登録した方は「LINEでログイン」をタップしてください。
                  <br />
                  このページをホーム画面に追加しておくと、次回から 1 タップで開けます。
                </Text>

                {/* パスワードリセットリンク
                    「新規登録」への導線は非表示 (2026-07-31)。現在はセールス主導フェーズで、
                    会社の新規作成は営業担当が /register を直接開いて行う (成約後カード参照)。
                    セルフサーブ (Free β 公開) に切り替える際にリンクを復活させる。 */}
                <VStack spacing={3} pt={4}>
                  <HStack justify="flex-end" w="full">
                    <Button
                      variant="link"
                      color="blue.500"
                      fontSize="sm"
                      onClick={() => router.push('/forgot-password')}
                      _hover={{ textDecoration: 'underline' }}
                    >
                      パスワードを忘れた方
                    </Button>
                  </HStack>

                  {/* よくある質問（静的HTMLマニュアル・別タブ） */}
                  <Button
                    as="a"
                    href="/manual/faq.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="link"
                    color="gray.500"
                    fontSize="sm"
                    _hover={{ textDecoration: 'underline', color: 'gray.600' }}
                  >
                    よくある質問
                  </Button>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </Flex>
      </Box>
    </RequireGuest>
  );
};
export const getServerSideProps: GetServerSideProps = async () => {
  return { props: {} };
};

export default LoginPage; 